import { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import confetti from 'canvas-confetti';
import { Html5Qrcode } from 'html5-qrcode';
import SpinWheel from './games/SpinWheel';
import ScratchCard from './games/ScratchCard';
import MysteryBox from './games/MysteryBox';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GAME_TEXT = {
  roue: { action: 'Tourner la roue !', submit: 'Valider & tourner la roue', welcome: 'un nouveau tour de roue' },
  grattage: { action: 'Gratter la carte !', submit: 'Valider & gratter la carte', welcome: 'une nouvelle carte a gratter' },
  coffre: { action: 'Ouvrir un coffre !', submit: 'Valider & ouvrir un coffre', welcome: 'un nouveau coffre a ouvrir' },
};

const RoueDuMas = () => {
  const [screen, setScreen] = useState('register');
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', dateNaissance: '', telephone: '', adresse: '', cgv: false });
  const [errors, setErrors] = useState({});
  const [clientId, setClientId] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [prizes, setPrizes] = useState([]);
  const [validityDays, setValidityDays] = useState(30);
  const [gameActive, setGameActive] = useState(true);
  const [gameType, setGameType] = useState('roue');
  const [cgvOpen, setCgvOpen] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenChecking, setTokenChecking] = useState(true);

  useEffect(() => {
    // Check weekly token from URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('t');
    if (token) {
      fetch(`${API}/roue/verify-token/${token}`).then(r => r.json()).then(data => {
        setTokenValid(data.valid);
        setTokenChecking(false);
      }).catch(() => { setTokenValid(false); setTokenChecking(false); });
    } else {
      // No token = direct URL access, allow it (backwards compatible)
      setTokenValid(true);
      setTokenChecking(false);
    }

    fetch(`${API}/roue/prizes`).then(r => r.json()).then(data => {
      setPrizes(data.prizes || []);
      setValidityDays(data.validity_days || 30);
      setGameActive(data.game_active !== false);
      setGameType(data.game_type || 'roue');
    }).catch(() => {});
  }, []);

  const reset = () => {
    setScreen('register');
    setForm({ prenom: '', nom: '', email: '', dateNaissance: '', telephone: '', adresse: '', cgv: false });
    setErrors({});
    setClientId(null);
    setVoucher(null);
  };

  return (
    <div className="min-h-screen bg-[#1a2744] flex items-center justify-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <div className="w-full max-w-lg mx-auto px-4 py-8">
        {tokenChecking ? (
          <div className="text-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[#0d9488] border-t-transparent rounded-full mx-auto" />
          </div>
        ) : tokenValid === false ? (
          <div className="text-center py-12 px-6">
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">⏰</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>QR Code expire</h2>
            <p className="text-white/50 text-sm mb-6">Ce QR code n'est plus valide. Demandez le nouveau QR code au restaurant pour jouer cette semaine !</p>
            <p className="text-[#0d9488] text-xs font-medium">Le Mas — Restaurant & Traiteur</p>
          </div>
        ) : (
          <>
        {screen === 'register' && (
          <RegisterScreen form={form} setForm={setForm} errors={errors} setErrors={setErrors}
            setClientId={setClientId} setScreen={setScreen} loading={loading} setLoading={setLoading}
            gameActive={gameActive} setCgvOpen={setCgvOpen} gameType={gameType} validityDays={validityDays} />
        )}
        {screen === 'game' && gameType === 'roue' && (
          <SpinWheel clientId={clientId} prizes={prizes} setVoucher={setVoucher} setScreen={setScreen} />
        )}
        {screen === 'game' && gameType === 'grattage' && (
          <ScratchCard clientId={clientId} prizes={prizes} setVoucher={setVoucher} setScreen={setScreen} />
        )}
        {screen === 'game' && gameType === 'coffre' && (
          <MysteryBox clientId={clientId} prizes={prizes} setVoucher={setVoucher} setScreen={setScreen} />
        )}
        {screen === 'voucher' && (
          <VoucherScreen voucher={voucher} onFinish={reset} validityDays={validityDays} />
        )}
        {screen === 'staff-login' && (
          <StaffLogin setScreen={setScreen} />
        )}
        {screen === 'staff' && (
          <StaffDashboard setScreen={setScreen} />
        )}
        </>
        )}

      {cgvOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" data-testid="cgv-modal">
          <div className="bg-[#243559] border border-[#0d9488]/20 rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg text-[#0d9488] font-bold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Conditions Generales
            </h3>
            <div className="text-[white]/70 text-xs leading-relaxed space-y-3">
              <p><strong>Jeu "La Roue du Mas"</strong> — Organise par Le Mas, restaurant situe a Saint-Laurent-de-la-Salanque (66250).</p>
              <p><strong>Participation :</strong> Le jeu est ouvert a toute personne physique majeure. La participation est limitee a une fois par jour et par personne.</p>
              <p><strong>Lots :</strong> Les lots gagnes sont valables pour une duree de {validityDays} jours a compter de la date de gain. Ils sont a utiliser sur place, lors d'une prochaine visite au restaurant. Les lots ne sont ni echangeables, ni remboursables.</p>
              <p><strong>Donnees personnelles :</strong> Les informations collectees (nom, prenom, email, telephone, date de naissance, adresse) sont necessaires a la gestion du jeu et de votre compte fidelite. Elles ne seront en aucun cas cedees a des tiers. Conformement au RGPD, vous disposez d'un droit d'acces, de rectification et de suppression de vos donnees en contactant le restaurant.</p>
              <p><strong>Contact :</strong> Le Mas — 04 68 38 06 98 — www.restaurant-le-mas.fr</p>
            </div>
            <button onClick={() => setCgvOpen(false)}
              className="w-full mt-5 py-2.5 rounded-xl bg-[#0d9488] text-[#1a2744] font-bold text-sm hover:bg-[#14b8a6] transition-all"
              data-testid="close-cgv">
              Fermer
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

/* ═══ SCREEN 1: Registration ═══ */
const RegisterScreen = ({ form, setForm, errors, setErrors, setClientId, setScreen, loading, setLoading, gameActive, setCgvOpen, gameType, validityDays }) => {
  const [step, setStep] = useState('choice');
  const [returning, setReturning] = useState(null);
  const gt = GAME_TEXT[gameType] || GAME_TEXT.roue;

  const checkEmail = async () => {
    const email = form.email.trim();
    if (!email || !email.includes('@')) {
      setErrors({ email: 'Email invalide' });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const res = await fetch(`${API}/roue/check-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');

      if (data.exists) {
        if (data.played_today) {
          setErrors({ general: 'Vous avez deja joue aujourd\'hui ! Revenez demain pour retenter votre chance.' });
        } else {
          setReturning({ prenom: data.prenom, nom: data.nom, client_id: data.client_id });
          setStep('welcome');
        }
      } else {
        setErrors({ general: 'Aucun compte trouve avec cet email. Cliquez sur "Premiere fois" pour vous inscrire.' });
      }
    } catch (err) {
      setErrors({ general: err.message });
    }
    setLoading(false);
  };

  const submitFull = async () => {
    const e = {};
    if (!form.prenom.trim()) e.prenom = 'Requis';
    if (!form.nom.trim()) e.nom = 'Requis';
    if (!form.email.trim() || !form.email.includes('@')) e.email = 'Email invalide';
    if (!form.telephone.trim()) e.telephone = 'Requis';
    if (!form.dateNaissance) e.dateNaissance = 'Requis';
    if (!form.cgv) e.cgv = 'Vous devez accepter les conditions';
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setLoading(true);
    try {
      const res = await fetch(`${API}/roue/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setClientId(data.client_id);
      setScreen('game');
    } catch (err) {
      setErrors({ general: err.message });
    }
    setLoading(false);
  };

  const goAsReturning = () => {
    if (returning) {
      setClientId(returning.client_id);
      setScreen('game');
    }
  };

  return (
    <div className="text-center" data-testid="register-screen">
      <div className="mb-6">
        <img src="/images/logo-restaurant.jpg" alt="Le Mas" className="w-20 h-20 mx-auto rounded-full border-2 border-[#0d9488]/30 shadow-lg mb-4" />
        <h1 className="text-3xl font-bold text-[#0d9488] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          La Roue du Mas
        </h1>
        <p className="text-[white]/40 text-xs tracking-widest uppercase">Le Mas &middot; Saint-Laurent-de-la-Salanque</p>
        <p className="text-[white]/60 text-sm mt-2">Tentez votre chance et gagnez un cadeau !</p>
      </div>

      {!gameActive ? (
        <div className="bg-[#243559] border border-[#0d9488]/20 rounded-xl p-6 text-center">
          <p className="text-[white]/60 text-sm">Le jeu est actuellement en pause. Revenez bientot !</p>
        </div>
      ) : step === 'choice' ? (
        <div className="space-y-3">
          <button onClick={() => { setStep('full'); setErrors({}); }}
            className="w-full py-4 rounded-xl bg-[#0d9488] text-[#1a2744] font-bold text-base tracking-wide hover:bg-[#14b8a6] transition-all"
            style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="btn-premiere-fois">
            Premiere fois
          </button>
          <button onClick={() => { setStep('returning'); setErrors({}); }}
            className="w-full py-4 rounded-xl border-2 border-[#0d9488]/40 text-[#0d9488] font-bold text-base tracking-wide hover:bg-[#0d9488]/10 transition-all"
            style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="btn-deja-client">
            Deja client
          </button>
        </div>

      ) : step === 'returning' ? (
        <>
          <p className="text-[white]/60 text-sm mb-4">Entrez votre email pour jouer :</p>
          <div className="space-y-4 text-left">
            <Field label="Votre email" type="email" value={form.email}
              onChange={v => setForm(f => ({ ...f, email: v }))} error={errors.email} testId="input-email" />
          </div>

          {errors.general && (
            <div className="bg-[#243559] border border-[#0d9488]/20 rounded-xl p-4 mt-4 text-center">
              <p className="text-[#0d9488] text-sm">{errors.general}</p>
            </div>
          )}

          <button onClick={checkEmail} disabled={loading}
            className="w-full mt-6 py-3.5 rounded-xl bg-[#0d9488] text-[#1a2744] font-bold text-base tracking-wide hover:bg-[#14b8a6] transition-all disabled:opacity-50"
            style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="check-email-btn">
            {loading ? 'Verification...' : 'C\'est parti !'}
          </button>
          <button onClick={() => { setStep('choice'); setErrors({}); }}
            className="mt-3 text-[white]/40 text-xs hover:text-[white]/60 transition-colors" data-testid="back-to-choice">
            &larr; Retour
          </button>
        </>

      ) : step === 'welcome' && returning ? (
        <>
          <div className="bg-[#243559] border border-[#0d9488]/20 rounded-2xl p-8 mb-4">
            <p className="text-3xl mb-3">&#128075;</p>
            <h2 className="text-xl text-[#0d9488] font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Content de vous revoir, {returning.prenom} !
            </h2>
            <p className="text-[white]/60 text-sm">Vous avez droit a {gt.welcome} aujourd'hui.</p>
          </div>

          <button onClick={goAsReturning}
            className="w-full py-3.5 rounded-xl bg-[#0d9488] text-[#1a2744] font-bold text-base tracking-wide hover:bg-[#14b8a6] transition-all"
            style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="play-again-btn">
            {gt.action}
          </button>
        </>

      ) : step === 'full' ? (
        <>
          <p className="text-[white]/60 text-sm mb-4">Inscrivez-vous pour jouer :</p>
          <div className="space-y-4 text-left">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prenom *" value={form.prenom} onChange={v => setForm(f => ({ ...f, prenom: v }))} error={errors.prenom} testId="input-prenom" />
              <Field label="Nom *" value={form.nom} onChange={v => setForm(f => ({ ...f, nom: v }))} error={errors.nom} testId="input-nom" />
            </div>
            <Field label="Email *" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} error={errors.email} testId="input-email-full" />
            <Field label="Telephone *" type="tel" value={form.telephone} onChange={v => setForm(f => ({ ...f, telephone: v }))} error={errors.telephone} testId="input-telephone" placeholder="06 12 34 56 78" />
            <Field label="Adresse" value={form.adresse} onChange={v => setForm(f => ({ ...f, adresse: v }))} testId="input-adresse" placeholder="Facultatif" />
            <Field label="Date de naissance *" type="date" value={form.dateNaissance} onChange={v => setForm(f => ({ ...f, dateNaissance: v }))} error={errors.dateNaissance} testId="input-dob" />

            <div>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={form.cgv} onChange={e => { setForm(f => ({ ...f, cgv: e.target.checked })); setErrors(er => ({ ...er, cgv: undefined })); }}
                  className="mt-0.5 w-4 h-4 rounded border-[#0d9488]/30 accent-[#0d9488] bg-[#243559] shrink-0" data-testid="input-cgv" />
                <span className="text-[white]/60 text-xs leading-relaxed">
                  J'accepte les <a href="#cgv" className="text-[#0d9488] underline" onClick={e => { e.preventDefault(); setCgvOpen(true); }}>conditions generales</a> et la collecte de mes donnees personnelles dans le cadre de ce jeu. *
                </span>
              </label>
              {errors.cgv && <p className="text-[#dc2626] text-xs mt-1 ml-6">{errors.cgv}</p>}
            </div>
          </div>

          {errors.general && <p className="text-[#dc2626] text-sm mt-3">{errors.general}</p>}

          <button onClick={submitFull} disabled={loading}
            className="w-full mt-6 py-3.5 rounded-xl bg-[#0d9488] text-[#1a2744] font-bold text-base tracking-wide hover:bg-[#14b8a6] transition-all disabled:opacity-50"
            style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="submit-register">
            {loading ? 'Chargement...' : gt.submit}
          </button>
          <button onClick={() => { setStep('choice'); setErrors({}); }}
            className="mt-3 text-[white]/40 text-xs hover:text-[white]/60 transition-colors" data-testid="back-to-choice-full">
            &larr; Retour
          </button>
        </>
      ) : null}

      <button onClick={() => setScreen('staff-login')}
        className="mt-6 text-[white]/30 text-xs hover:text-[white]/50 transition-colors" data-testid="staff-access">
        Acces equipe
      </button>
    </div>
  );
};

const Field = ({ label, type = 'text', value, onChange, error, testId, placeholder }) => (
  <div>
    <label className="block text-[white]/70 text-xs mb-1 tracking-wide uppercase">{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''}
      className={`w-full px-4 py-3 rounded-lg bg-[#243559] border text-[white] text-sm outline-none transition-colors
        ${error ? 'border-[#dc2626]' : 'border-[#0d9488]/20 focus:border-[#0d9488]/60'}`}
      data-testid={testId} />
    {error && <p className="text-[#dc2626] text-xs mt-1">{error}</p>}
  </div>
);

/* ═══ SCREEN 3: Voucher ═══ */
const VoucherScreen = ({ voucher, onFinish, validityDays }) => {
  useEffect(() => {
    const burst = () => confetti({ particleCount: 80, spread: 70, colors: ['#0d9488', '#1a2744', '#14b8a6', '#ffffff'], origin: { y: 0.6 } });
    burst();
    const t = setTimeout(burst, 700);
    return () => clearTimeout(t);
  }, []);

  if (!voucher) return null;

  return (
    <div className="text-center" data-testid="voucher-screen">
      <img src="/images/logo-restaurant.jpg" alt="Le Mas" className="w-16 h-16 mx-auto rounded-full border-2 border-[#0d9488]/30 shadow-lg mb-4" />
      <h2 className="text-2xl font-bold text-[#0d9488] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Felicitations !
      </h2>
      <p className="text-[white] text-lg mb-6">{voucher.prize_label}</p>

      <div className="bg-[#243559] border border-[#0d9488]/20 rounded-2xl p-6 mb-6">
        <p className="text-[white]/50 text-xs uppercase tracking-widest mb-2">Votre code</p>
        <p className="text-[#0d9488] text-2xl font-mono font-bold tracking-wider mb-5" data-testid="voucher-code">
          {voucher.code}
        </p>
        <div className="flex justify-center mb-4">
          <QRCodeSVG value={voucher.code} size={180} bgColor="#243559" fgColor="#0d9488"
            level="M" data-testid="voucher-qr" />
        </div>
        <p className="text-[white]/50 text-xs leading-relaxed">
          Montrez ce QR code a votre serveuse lors de votre prochaine venue.<br />
          Valable {validityDays || 30} jours.
        </p>
      </div>

      <button onClick={onFinish}
        className="w-full py-3 rounded-xl border border-[#0d9488]/30 text-[#0d9488] font-medium hover:bg-[#0d9488]/10 transition"
        style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="finish-button">
        Terminer
      </button>
    </div>
  );
};

/* ═══ SCREEN 4: Staff PIN Login ═══ */
const StaffLogin = ({ setScreen }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleDigit = (d) => {
    if (pin.length >= 4) return;
    const newPin = pin + d;
    setPin(newPin);
    setError('');
    if (newPin.length === 4) {
      fetch(`${API}/roue/staff-login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: newPin }),
      }).then(r => {
        if (r.ok) setScreen('staff');
        else { setError('Code incorrect'); setPin(''); }
      }).catch(() => { setError('Erreur connexion'); setPin(''); });
    }
  };

  return (
    <div className="text-center" data-testid="staff-login-screen">
      <button onClick={() => setScreen('register')} className="text-[white]/40 text-xs mb-6 hover:text-[white]/60" data-testid="back-to-register">
        &larr; Retour
      </button>
      <img src="/images/logo-restaurant.jpg" alt="Le Mas" className="w-14 h-14 mx-auto rounded-full border-2 border-[#0d9488]/30 shadow-lg mb-4" />
      <h2 className="text-xl font-bold text-[#0d9488] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Acces Equipe
      </h2>
      <div className="flex justify-center gap-3 mb-6">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${
            i < pin.length ? 'bg-[#0d9488] border-[#0d9488]' : 'border-[#0d9488]/30'
          }`} data-testid={`pin-dot-${i}`} />
        ))}
      </div>
      {error && <p className="text-[#dc2626] text-sm mb-4" data-testid="pin-error">{error}</p>}
      <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'del'].map((d, i) => (
          d === null ? <div key={i} /> :
          d === 'del' ? (
            <button key={i} onClick={() => setPin(p => p.slice(0, -1))}
              className="h-14 rounded-xl bg-[#243559] text-[white]/50 text-sm hover:bg-[#243559] transition" data-testid="pin-delete">
              &larr;
            </button>
          ) : (
            <button key={i} onClick={() => handleDigit(String(d))}
              className="h-14 rounded-xl bg-[#243559] text-[white] text-lg font-medium hover:bg-[#243559] transition border border-[#0d9488]/10"
              data-testid={`pin-key-${d}`}>
              {d}
            </button>
          )
        ))}
      </div>
    </div>
  );
};

/* ═══ SCREEN 5: Staff Dashboard ═══ */
const StaffDashboard = ({ setScreen }) => {
  const [tab, setTab] = useState('scanner');

  return (
    <div className="max-w-2xl mx-auto" data-testid="staff-dashboard">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <img src="/images/logo-restaurant.jpg" alt="Le Mas" className="w-10 h-10 rounded-full border border-[#0d9488]/20" />
          <h2 className="text-lg font-bold text-[#0d9488]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Tableau de bord
          </h2>
        </div>
        <button onClick={() => setScreen('register')} className="text-[white]/40 text-xs hover:text-[white]/60" data-testid="staff-logout">
          Deconnexion
        </button>
      </div>

      <div className="flex gap-1 p-1 rounded-lg bg-[#243559] mb-5">
        {[{ id: 'scanner', label: 'Scanner' }, { id: 'clients', label: 'Clients' }, { id: 'stats', label: 'Stats' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
              tab === t.id ? 'bg-[#0d9488]/15 text-[#0d9488]' : 'text-[white]/50 hover:text-[white]/80'
            }`} data-testid={`staff-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'scanner' && <ScannerTab />}
      {tab === 'clients' && <ClientsTab />}
      {tab === 'stats' && <StatsTab />}
    </div>
  );
};

/* ═══ Tab 1: Scanner ═══ */
const ScannerTab = () => {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const scannerDivId = 'qr-reader-roue';

  const validate = async (c) => {
    const trimmed = (c || '').trim().toUpperCase();
    if (!trimmed) return;
    setResult(null); setError('');
    try {
      const res = await fetch(`${API}/roue/validate/${trimmed}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        setCode('');
      } else {
        setError(data.detail || 'Code non trouve');
      }
    } catch { setError('Erreur connexion'); }
  };

  const startScan = async () => {
    if (scannerRef.current) return;
    try {
      const scanner = new Html5Qrcode(scannerDivId);
      scannerRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (text) => {
          stopScan();
          validate(text);
        },
        () => {}
      );
    } catch (err) {
      setError('Camera non disponible: ' + err.message);
      setScanning(false);
    }
  };

  const stopScan = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current.clear().catch(() => {});
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => () => stopScan(), [stopScan]);

  return (
    <div data-testid="scanner-tab">
      <div className="mb-4">
        {!scanning ? (
          <button onClick={startScan}
            className="w-full py-3 rounded-xl border border-[#0d9488]/30 text-[#0d9488] text-sm font-medium hover:bg-[#0d9488]/10 transition"
            data-testid="start-scan">
            Ouvrir le scanner
          </button>
        ) : (
          <button onClick={stopScan}
            className="w-full py-2 rounded-xl bg-[#dc2626]/20 text-[#dc2626] text-sm mb-2" data-testid="stop-scan">
            Fermer le scanner
          </button>
        )}
        <div id={scannerDivId} className="rounded-xl overflow-hidden mt-2"
          style={{ border: scanning ? '2px solid #0d9488' : 'none' }} />
      </div>

      <div className="flex gap-2 mb-4">
        <input value={code} onChange={e => setCode(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && validate(code)}
          placeholder="MAS-CAFE-1234" className="flex-1 px-4 py-2.5 rounded-lg bg-[#243559] border border-[#0d9488]/20 text-[white] text-sm font-mono outline-none"
          data-testid="manual-code-input" />
        <button onClick={() => validate(code)}
          className="px-5 py-2.5 rounded-lg bg-[#0d9488] text-[#1a2744] font-bold text-sm hover:bg-[#14b8a6] transition"
          data-testid="validate-code">Valider</button>
      </div>

      {error && (
        <div className="bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl p-4 text-center mb-4" data-testid="scan-error">
          <p className="text-[#dc2626] text-sm">{error}</p>
        </div>
      )}

      {result && (
        <div className="bg-[#14b8a6]/10 border border-[#14b8a6]/30 rounded-xl p-5 text-center" data-testid="scan-success">
          <p className="text-2xl mb-2">&#10003;</p>
          <p className="text-[#14b8a6] font-bold text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Bon valide !
          </p>
          <p className="text-[white] text-sm mt-2">
            {result.client?.prenom} {result.client?.nom}
          </p>
          <p className="text-[#0d9488] text-sm mt-1">
            {result.prize_emoji} {result.prize_label}
          </p>
        </div>
      )}
    </div>
  );
};

/* ═══ Tab 2: Clients ═══ */
const ClientsTab = () => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/roue/clients?search=${search}`)
      .then(r => r.json()).then(setClients).catch(() => {})
      .finally(() => setLoading(false));
  }, [search]);

  return (
    <div data-testid="clients-tab">
      <div className="flex gap-2 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
          className="flex-1 px-4 py-2 rounded-lg bg-[#243559] border border-[#0d9488]/20 text-[white] text-sm outline-none"
          data-testid="clients-search" />
        <a href={`${API}/roue/clients/export-csv`} target="_blank" rel="noreferrer"
          className="px-4 py-2 rounded-lg border border-[#0d9488]/20 text-[#0d9488] text-xs font-medium hover:bg-[#0d9488]/10 transition flex items-center"
          data-testid="export-csv">CSV</a>
      </div>

      {loading ? (
        <p className="text-[white]/40 text-center py-8 text-sm">Chargement...</p>
      ) : clients.length === 0 ? (
        <p className="text-[white]/40 text-center py-8 text-sm">Aucun client</p>
      ) : (
        <div className="space-y-1">
          {clients.map(c => (
            <div key={c.id}>
              <button onClick={() => setSelected(selected === c.id ? null : c.id)}
                className="w-full bg-[#243559] rounded-lg px-4 py-3 text-left hover:bg-[#243559] transition border border-transparent hover:border-[#0d9488]/10"
                data-testid={`client-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[white] text-sm font-medium">{c.prenom} {c.nom}</p>
                    <p className="text-[white]/40 text-xs">{c.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#0d9488] text-xs font-mono">{c.nb_vouchers} bons</p>
                    <p className="text-[white]/30 text-[10px]">{c.created_at?.slice(0, 10)}</p>
                  </div>
                </div>
              </button>
              {selected === c.id && c.vouchers?.length > 0 && (
                <div className="bg-[#1a2744] border border-[#0d9488]/10 rounded-lg mx-2 mb-1 p-3 space-y-1.5">
                  {c.vouchers.map(v => (
                    <div key={v.id} className="flex items-center justify-between text-xs">
                      <span className="text-[white]/60">{v.prize_emoji} {v.prize_label}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[#0d9488]/70">{v.code}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${v.used ? 'bg-[#14b8a6]/20 text-[#14b8a6]' : 'bg-[#0d9488]/10 text-[#0d9488]'}`}>
                          {v.used ? 'Utilise' : 'Actif'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══ Tab 3: Stats ═══ */
const StatsTab = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/roue/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  if (!stats) return <p className="text-[white]/40 text-center py-8 text-sm">Chargement...</p>;

  const maxPrize = Math.max(...(stats.prize_distribution || []).map(p => p.count), 1);

  return (
    <div className="space-y-4" data-testid="stats-tab">
      <div className="grid grid-cols-3 gap-2">
        <KpiCard label="Clients" value={stats.total_clients} testId="stat-clients" />
        <KpiCard label="Bons emis" value={stats.total_vouchers} testId="stat-vouchers" />
        <KpiCard label="Utilises" value={`${stats.taux_utilisation}%`} testId="stat-taux" />
      </div>

      {stats.prize_distribution?.length > 0 && (
        <div className="bg-[#243559] rounded-xl p-4">
          <p className="text-[white]/40 text-[10px] uppercase tracking-widest mb-3">Distribution des lots</p>
          <div className="space-y-2">
            {stats.prize_distribution.map(p => (
              <div key={p.prize_id} className="flex items-center gap-3">
                <span className="text-sm w-5">{p.emoji}</span>
                <span className="text-[white]/70 text-xs w-32 truncate">{p.label}</span>
                <div className="flex-1 bg-[#1a2744] rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-[#0d9488] rounded-full transition-all" style={{ width: `${(p.count / maxPrize) * 100}%` }} />
                </div>
                <span className="text-[#0d9488] text-xs font-mono w-6 text-right">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.recent?.length > 0 && (
        <div className="bg-[#243559] rounded-xl p-4">
          <p className="text-[white]/40 text-[10px] uppercase tracking-widest mb-3">Derniers bons</p>
          <div className="space-y-1.5">
            {stats.recent.map(v => (
              <div key={v.id} className="flex items-center justify-between text-xs py-1">
                <div className="flex items-center gap-2">
                  <span>{v.prize_emoji}</span>
                  <span className="text-[white]/70">{v.client_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[#0d9488]/60">{v.code}</span>
                  <span className={`w-2 h-2 rounded-full ${v.used ? 'bg-[#14b8a6]' : 'bg-[#0d9488]'}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const KpiCard = ({ label, value, testId }) => (
  <div className="bg-[#243559] rounded-xl p-4 text-center">
    <p className="text-[white]/40 text-[10px] uppercase tracking-widest mb-1">{label}</p>
    <p className="text-[#0d9488] text-xl font-bold font-mono" data-testid={testId}>{value}</p>
  </div>
);

export default RoueDuMas;
